/**
=========================================================
* Material Dashboard 3 PRO React - v2.3.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
 * The base colors for the Material Dashboard 3 PRO React.
 * You can add new color using this file.
 * You can customized the colors for the entire Material Dashboard 3 PRO React using thie file.
 */

const colors = {
  background: {
    default: "#f5f5f5",
    sidenav: "#336e6e", // lighter teal/green for sidebar (SBI)
  },

  backgroundSbi: {
    default: "#1a4d4d",
    secondary: "#336e6e",
  },

  /** SBI dashboard/header light & dark theme (toggled via context sbiDashboardTheme). */
  sbiDashboardTheme: {
    dark: {
      bgMain: "#1a1f23",
      bgHeader: "#1e2d2f",
      border: "rgba(255,255,255,0.12)",
      textPrimary: "#ffffff",
      textSecondary: "rgba(255,255,255,0.85)",
      link: "rgba(255,255,255,0.85)",
    },
    light: {
      bgMain: "#ffffff",
      bgHeader: "#e8f2f3",
      border: "#d1e0e2",
      textPrimary: "#1a3d40",
      textSecondary: "#5a7a7d",
      link: "#1f4e50",
    },
  },

  accentSbi: {
    tealLight: "#8fe7e3", // soft highlight (chips, tags, subtle glow)
    cyan: "#4ecbff", // “tech” accent (links, info states, charts)
    tealAccent: "#3fe0d6", // punchy accent (badges, active indicators)
  },

  primarySbi: {
    main: "#24b3a6", // main CTA / primary actions (works great on your dark teal)
    focus: "#1b8080", // hover/active (slightly darker)
  },

  secondarySbi: {
    main: "#7eadd7", // cool SBI-like blue (secondary actions, info surfaces)
    focus: "#4a6093", // deeper slate-blue for hover/active
  },
  borderSbi: {
    subtle: "#3b7a7a",
    strong: "#5aa0a0",
  },

  surfaceSbi: {
    card: "#204f4f",
    cardHover: "#255a5a",
    elevated: "#2b6363",
  },

  statusSbi: {
    success: "#35c79a",
    warning: "#ffb020",
    error: "#ff5a5f",
    info: "#4ecbff",
  },

  textSbi: {
    primary: "#e8f6f6",
    secondary: "#b6d7d7",
    onPrimary: "#0b1f1f", // recommended on top of primary.main
    onSecondary: "#0b1f1f", // recommended on top of secondary.main
  },

  greySbi: {
    100: "#f1f7f6", // very light, slightly teal-tinted
    200: "#e1eeec",
    300: "#c8ddda",
    400: "#a6c2be",
    500: "#7f9a96", // balanced “mid” grey with greenish undertone
    600: "#5e7773",
    700: "#445e5a",
    800: "#2b4240",
    900: "#162a29", // near-black, still greenish
  },

  accent: {
    tealLight: "#4db6ac",
    cyan: "#00acc1",
    tealAccent: "#26a69a",
  },

  text: {
    main: "#737373",
    focus: "#737373",
  },

  transparent: {
    main: "transparent",
  },

  white: {
    main: "#ffffff",
    focus: "#ffffff",
  },

  black: {
    light: "#000000",
    main: "#000000",
    focus: "#000000",
  },

  primary: {
    main: "#1a4d4d",
    focus: "#1a4d4d",
  },

  secondary: {
    main: "#7b809a",
    focus: "#8f93a9",
  },

  info: {
    main: "#262626",
    focus: "#262626",
  },

  success: {
    main: "#4CAF50",
    focus: "#67bb6a",
  },

  warning: {
    main: "#fb8c00",
    focus: "#fc9d26",
  },

  error: {
    main: "#F44335",
    focus: "#f65f53",
  },

  low: {
    main: "#ff5722",
    focus: "#e64a19",
  },

  medium: {
    main: "#ff9800",
    focus: "#f57c00",
  },

  high: {
    main: "#4caf50",
    focus: "#43a047",
  },

  light: {
    main: "#f0f2f5",
    focus: "#f0f2f5",
  },

  dark: {
    main: "#171717",
    focus: "#171717",
  },

  grey: {
    100: "#f5f5f5",
    200: "#e5e5e5",
    300: "#d4d4d4",
    400: "#a3a3a3",
    500: "#737373",
    600: "#525252",
    700: "#404040",
    800: "#262626",
    900: "#171717",
  },

  gradients: {
    primary: {
      main: "#EC407A",
      state: "#D81B60",
    },

    secondary: {
      main: "#747b8a",
      state: "#495361",
    },

    info: {
      main: "#42424a",
      state: "#191919",
    },

    success: {
      main: "#66BB6A",
      state: "#43A047",
    },

    warning: {
      main: "#FFA726",
      state: "#FB8C00",
    },

    error: {
      main: "#EF5350",
      state: "#E53935",
    },

    light: {
      main: "#EBEFF4",
      state: "#CED4DA",
    },

    dark: {
      main: "#42424a",
      state: "#191919",
    },
  },

  socialMediaColors: {
    facebook: {
      main: "#3b5998",
      dark: "#344e86",
    },

    twitter: {
      main: "#55acee",
      dark: "#3ea1ec",
    },

    instagram: {
      main: "#125688",
      dark: "#0e456d",
    },

    linkedin: {
      main: "#0077b5",
      dark: "#00669c",
    },

    pinterest: {
      main: "#cc2127",
      dark: "#b21d22",
    },

    youtube: {
      main: "#e52d27",
      dark: "#d41f1a",
    },

    vimeo: {
      main: "#1ab7ea",
      dark: "#13a3d2",
    },

    slack: {
      main: "#3aaf85",
      dark: "#329874",
    },

    dribbble: {
      main: "#ea4c89",
      dark: "#e73177",
    },

    github: {
      main: "#24292e",
      dark: "#171a1d",
    },

    reddit: {
      main: "#ff4500",
      dark: "#e03d00",
    },

    tumblr: {
      main: "#35465c",
      dark: "#2a3749",
    },
  },

  badgeColors: {
    primary: {
      background: "#f8b3ca",
      text: "#cc084b",
    },

    secondary: {
      background: "#d7d9e1",
      text: "#6c757d",
    },

    info: {
      background: "#aecef7",
      text: "#095bc6",
    },

    success: {
      background: "#bce2be",
      text: "#339537",
    },

    warning: {
      background: "#ffd59f",
      text: "#c87000",
    },

    error: {
      background: "#fcd3d0",
      text: "#f61200",
    },

    light: {
      background: "#ffffff",
      text: "#c7d3de",
    },

    dark: {
      background: "#8097bf",
      text: "#1e2e4a",
    },
  },

  coloredShadows: {
    primary: "#e91e62",
    secondary: "#110e0e",
    info: "#191919",
    success: "#4caf4f",
    warning: "#ff9900",
    error: "#f44336",
    light: "#adb5bd",
    dark: "#404040",
  },

  inputBorderColor: "#d2d6da",

  tabs: {
    indicator: { boxShadow: "#ddd" },
  },
};

/** SBI header/content colors for dark mode (use with sbiDashboardTheme context). */
export const SBI_DARK = colors.sbiDashboardTheme.dark;

/** SBI header/content colors for light mode (use with sbiDashboardTheme context). */
export const SBI_LIGHT = colors.sbiDashboardTheme.light;

export default colors;
